import { useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import type { PDPStockLevel as PdpStockIndicatorSchema } from '@amplience/content-types/typings/c-pdp-stock-level';
import type {
  AppointmentBookingForm as AppointmentBookingFormSchema,
  MeasureAtHome as MeasureAtHomeSchema,
  TextAndCopyBlock as TextAndCopyBlockSchema,
} from '@amplience/content-types/typings/c-size-guide';
import type { FlexibleText } from '@amplience/content-types/typings/p-partials';
import type { StockLevel } from '@amplience/content-types/typings/s-pdp-layout';
import { ProductTypesEnum } from '@ct/types';
import { getItem, useFeatureFlagsContext } from '@storefront/lib-global';
import { ProductReferenceType } from '@storefront/lib-global/codegen/types';
import type { ProductReference } from '@storefront/lib-global/codegen/types';
import { useLocaleContext } from '@storefront/lib-global/context/localeContext';
import { useDisableBodyScroll } from '@storefront/lib-global/hooks/useDisableBodyScroll';
import { useStar2 } from '@storefront/lib-global/hooks/useStar2';
import { TypographyStylesType, colours } from '@storefront/lib-global/stylings';
import { getLocalizedValue } from '@storefront/lib-global/utils/transformers';
import { useSizeAndWidthContext } from '../../../context';
import { useGetSizeGuide, useStockAvailabilityInfo } from '../../../hooks';
import {
  areAllVariantsWithNotificationsDisabledOrInStock,
  areSizeAndWidthDisplayed,
  getAvailableStockQuantity,
  isEveryVariantInStock,
} from '../../../utils';
import SizeSelector from '../SizeSelector/SizeSelector';
import { StockIndicator } from '../StockIndicator';
import WidthSelector from '../WidthSelector/WidthSelector';
import AddToBag from '../addToBag/AddToBag';
import { EmailMeWhenInStockForm } from '../emailMeWhenInStockForm/EmailMeWhenInStockForm';
import { EmailMeWhenInStockLink } from '../emailMeWhenInStockLink/EmailMeWhenInStockLink';
import { FindProductInStoreLink } from '../findProductInStoreLink/FindProductInStoreLink';
import { StockAvailabilityInfo } from '../stockAvailabilityInfo/StockAvailabilityInfo';
import {
  ActionButtonsContainer,
  SizeAndWidthSelectorContainer,
  SizeGuideButton,
  SizeHeadDescription,
  SizeHeadText,
  SizeSelectorContainer,
  SizeTextWrapper,
} from './SizeAndWithSelectors.styles';

const DynamicSizeGuidePopUp = dynamic(
  async () => {
    const { SizeGuide } = await import('../SizeGuidePopUp/SizeGuidePopUp');
    return SizeGuide;
  },
  { ssr: false },
);

const DynamicStoreStockPopUp = dynamic(
  async () => {
    const { StoreStock } = await import('../storeStockPopUp/StoreStockPopUp');
    return StoreStock;
  },
  { ssr: false },
);

const DynamicNotifyMePopUp = dynamic(
  async () => {
    const { NotifyMePopUp } = await import('../notifyMePopUp/NotifyMePopUp');
    return NotifyMePopUp;
  },
  { ssr: false },
);

type SizeAndWidthSelectorsProps = {
  stockLevel: StockLevel | undefined;
  sizeGuideTitle: FlexibleText;
  references: ProductReference[] | undefined;
  gender: string[] | undefined;
  type: string;
  productImageURL: string;
  productName: string;
  productColor: string;
  measureAtHome: MeasureAtHomeSchema | null | undefined;
  bookingForm: AppointmentBookingFormSchema | null | undefined;
  copyBlock: TextAndCopyBlockSchema | null | undefined;
  productKey: string;
};

export function SizeAndWidthSelectors({
  stockLevel,
  sizeGuideTitle,
  references,
  gender,
  type,
  productImageURL,
  productName,
  productColor,
  measureAtHome,
  bookingForm,
  copyBlock,
}: SizeAndWidthSelectorsProps) {
  const { t } = useTranslation('lib-products-common');
  const { locale } = useLocaleContext();
  const { isProductStockCheckEnabled } = useFeatureFlagsContext();
  const { isStar2 } = useStar2();
  const {
    isSubdomainOutlet,
    selectedSize,
    selectedWidth,
    selectedSku,
    variants,
    isNotifyMeOpen,
    uniqueSizes,
    noResultsFound,
    isProductSoldOutOnline,
    isSelectedVariantOutOfStock,
    isSelectedVariantWithNotificationEnabled,
  } = useSizeAndWidthContext();
  const { getSizeGuide, isSizeGuideOpen, setIsSizeGuideOpen, sizeGuide, isCalled } = useGetSizeGuide();
  const availabilityInfo = useStockAvailabilityInfo();
  const [isStoreStockOpen, setIsStoreStockOpen] = useState(false);

  const inventory = getAvailableStockQuantity(variants, selectedSku, selectedWidth, selectedSize);
  const allVariantsInStock = isEveryVariantInStock(variants);

  const largerSizeProduct: ProductReference | undefined = useMemo(
    () => references?.find((reference) => reference.type === ProductReferenceType.LARGERSIZES),
    [references],
  );
  const smallerSizeProduct: ProductReference | undefined = useMemo(
    () => references?.find((reference) => reference.type === ProductReferenceType.SMALLERSIZES),
    [references],
  );

  const allVariantsNotificationsDisabledOrInStock = areAllVariantsWithNotificationsDisabledOrInStock(variants);
  const isNotifyMePopUpOpen = isNotifyMeOpen && noResultsFound && isSelectedVariantWithNotificationEnabled;
  const isEmailMeWhenInStockVisible =
    (!isSubdomainOutlet &&
      !allVariantsInStock &&
      !selectedSku &&
      !isProductSoldOutOnline &&
      !allVariantsNotificationsDisabledOrInStock) ||
    (!isProductSoldOutOnline && isSelectedVariantOutOfStock && isSelectedVariantWithNotificationEnabled);
  const isEmailMeWhenInStockFormVisible =
    !isProductSoldOutOnline &&
    isSelectedVariantOutOfStock &&
    isSelectedVariantWithNotificationEnabled &&
    !noResultsFound &&
    selectedSku;
  const isAddToBagVisible = !isProductSoldOutOnline && !isSelectedVariantOutOfStock;

  const toggleSizeGuide = () => {
    getSizeGuide({ gender, locale, type });
  };

  useDisableBodyScroll(isSizeGuideOpen || isStoreStockOpen || isNotifyMeOpen);

  const { pdpSelectedSize, pdpSelectedWidth, pdpSelectedSku } =
    getItem('pdpStockAvailabilityData', true, 'sessionStorage') || {};

  useEffect(() => {
    if (
      !pdpSelectedSize ||
      !pdpSelectedWidth ||
      !pdpSelectedSku ||
      !getItem('isPdpBackToResults', false, 'sessionStorage')
    )
      return;

    setIsStoreStockOpen(true);
  }, [pdpSelectedSize, pdpSelectedWidth, pdpSelectedSku]);

  return (
    <>
      {areSizeAndWidthDisplayed(variants, isSubdomainOutlet, largerSizeProduct, smallerSizeProduct) && (
        <>
          {isCalled && (
            <DynamicSizeGuidePopUp
              sizeGuide={sizeGuide}
              setisSizeGuideOpen={setIsSizeGuideOpen}
              isSizeGuideOpen={isSizeGuideOpen}
              bookingForm={bookingForm}
              measureAtHome={measureAtHome}
              copyBlock={copyBlock}
            />
          )}
          <SizeAndWidthSelectorContainer>
            {uniqueSizes?.length >= 1 && (
              <SizeSelectorContainer>
                <SizeTextWrapper aria-label={t('size.selector.select.your.size')}>
                  <SizeHeadText aria-hidden> {t('size.selector.select.your.size')}:</SizeHeadText>
                  {!isStar2 || type !== ProductTypesEnum.ACCESSORY ? (
                    <SizeGuideButton
                      $hasSizeGuideTitle={!!sizeGuideTitle}
                      onClick={toggleSizeGuide}
                      type="button"
                      aria-label={`Open ${getLocalizedValue(sizeGuideTitle?.text) || 'size guide'}`}
                      disabled={!sizeGuideTitle || !gender}
                      data-testid="sizeGuideLink"
                    >
                      <SizeHeadDescription
                        {...sizeGuideTitle}
                        defaultTextColor={colours.BLACK}
                        defaultTextSize={TypographyStylesType.CTA_TINY_SEMIBOLD}
                      />
                    </SizeGuideButton>
                  ) : null}
                </SizeTextWrapper>
                <SizeSelector largerSizeProduct={largerSizeProduct} smallerSizeProduct={smallerSizeProduct} />
              </SizeSelectorContainer>
            )}
            <WidthSelector />
          </SizeAndWidthSelectorContainer>
        </>
      )}
      <StockAvailabilityInfo
        message={availabilityInfo?.message || null}
        testId={availabilityInfo?.testId || null}
        size={availabilityInfo?.size || null}
        width={availabilityInfo?.width || null}
      />
      <StockIndicator
        inventory={inventory}
        isSubdomainOutlet={isSubdomainOutlet}
        {...(stockLevel as PdpStockIndicatorSchema)}
      />
      <ActionButtonsContainer>
        {isStoreStockOpen && (
          <DynamicStoreStockPopUp
            setIsStoreStockOpen={setIsStoreStockOpen}
            isStoreStockOpen={isStoreStockOpen}
            productImageURL={productImageURL}
            productName={productName}
            productColor={productColor}
            productSku={selectedSku}
          />
        )}
        {isNotifyMePopUpOpen && (
          <DynamicNotifyMePopUp
            productImageURL={productImageURL}
            productName={productName}
            productColor={productColor}
          />
        )}
        {isEmailMeWhenInStockVisible && <EmailMeWhenInStockLink />}
        {isProductStockCheckEnabled && !isStar2 && (
          <FindProductInStoreLink productName={productName} setIsStoreStockOpen={setIsStoreStockOpen} />
        )}
        {isAddToBagVisible && <AddToBag />}
      </ActionButtonsContainer>
      {isEmailMeWhenInStockFormVisible && <EmailMeWhenInStockForm productName={productName} />}
    </>
  );
}
